/*
 * i18n 入口：按需加载语言包，并把 Element UI 的内置文案桥接到同一套 message 上
 *
 * 语言包体积随语种线性增长，全部打进主包会拖慢登录首屏，
 * 因此每个语种拆成独立 chunk，切换时才拉取。
 */
import Vue from "vue";
import VueI18n from "vue-i18n";
import ElementLocale from "element-ui/lib/locale";
import { DEFAULT_LANGUAGE } from "@/extra/language";

Vue.use(VueI18n);

/*
 * 业务语言包与 Element UI 语言包合并成同一个 chunk，避免切换语言时发两次请求。
 * 路径必须字面量书写，webpack 3 无法分析拼接出来的 import()。
 * 注意 Element UI 2.13 没有马来语包，ms 退回英文；俄语的包名是 ru-RU 而非 ru。
 */
const loaders = {
  "zh-CN": () =>
    Promise.all([
      import(/* webpackChunkName: "lang-zh-CN" */ "./langs/zh-CN"),
      import(/* webpackChunkName: "lang-zh-CN" */ "element-ui/lib/locale/lang/zh-CN")
    ]),
  "zh-TW": () =>
    Promise.all([
      import(/* webpackChunkName: "lang-zh-TW" */ "./langs/zh-TW"),
      import(/* webpackChunkName: "lang-zh-TW" */ "element-ui/lib/locale/lang/zh-TW")
    ]),
  en: () =>
    Promise.all([
      import(/* webpackChunkName: "lang-en" */ "./langs/en"),
      import(/* webpackChunkName: "lang-en" */ "element-ui/lib/locale/lang/en")
    ]),
  es: () =>
    Promise.all([
      import(/* webpackChunkName: "lang-es" */ "./langs/es"),
      import(/* webpackChunkName: "lang-es" */ "element-ui/lib/locale/lang/es")
    ]),
  fr: () =>
    Promise.all([
      import(/* webpackChunkName: "lang-fr" */ "./langs/fr"),
      import(/* webpackChunkName: "lang-fr" */ "element-ui/lib/locale/lang/fr")
    ]),
  ms: () =>
    Promise.all([
      import(/* webpackChunkName: "lang-ms" */ "./langs/ms"),
      import(/* webpackChunkName: "lang-ms" */ "element-ui/lib/locale/lang/en")
    ]),
  ru: () =>
    Promise.all([
      import(/* webpackChunkName: "lang-ru" */ "./langs/ru"),
      import(/* webpackChunkName: "lang-ru" */ "element-ui/lib/locale/lang/ru-RU")
    ]),
  th: () =>
    Promise.all([
      import(/* webpackChunkName: "lang-th" */ "./langs/th"),
      import(/* webpackChunkName: "lang-th" */ "element-ui/lib/locale/lang/th")
    ])
};

const i18n = new VueI18n({
  // 只放占位语言：真实语言由 main.js 引导时一次性取回，再交给 loadLanguage 设置。
  // 挂载发生在 loadLanguage 之后，因此不会闪现默认语言文案
  locale: DEFAULT_LANGUAGE,
  fallbackLocale: DEFAULT_LANGUAGE,
  // 历史页面尚未接入 $t，缺失 key 的警告会把控制台刷满，这里静音
  silentTranslationWarn: true
});

// 桥接后 Element UI 组件内部的 t() 会读取当前 locale 的 el.* 文案，无需再单独设置 ELEMENT.locale
ElementLocale.i18n((key, value) => i18n.t(key, value));

const loaded = [];

// 幂等：重复切到同一语言只改 locale，不再发请求
export const loadLanguage = language => {
  const target = loaders[language] ? language : DEFAULT_LANGUAGE;
  if (loaded.indexOf(target) > -1) {
    i18n.locale = target;
    return Promise.resolve(target);
  }
  return loaders[target]().then(([messages, element]) => {
    // Element UI 的文案统一挂在 el 下，与业务命名空间同层合并
    i18n.setLocaleMessage(target, { ...messages.default, el: element.default.el });
    loaded.push(target);
    i18n.locale = target;
    return target;
  });
};

export default i18n;



// WEBPACK FOOTER //
// ./src/i18n/index.js